import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AccountSharedModule } from '@app/account/account-shared.module';
import { CollectionCardModule } from '@app/collections';
import { LibraryElementsModule } from '@app/library/library-elements.module';

import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { UserServiceCardFullModule } from '@app/meetings/user-service-card-full/user-service-card-full.module';
import { SchoolCardModule } from '@app/schools/school-card/school-card.module';
import { SchoolPageModule } from '@app/schools/school-page/school-page.module';
import { TeacherBookingSurfaceBlockComponent } from '@app/teachers/teacher-card/teacher-booking-surface-block/teacher-booking-surface-block.component';
import { TeacherCardModule } from '@app/teachers/teacher-card/teacher-card.module';
import { EmptyComponent, EmptyProps } from '@core/components/empty/empty.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { UserServiceCardModule } from '@core/components/service-card/user-service-card.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { PipesModule } from '@core/pipes/pipes.module';
import { FeaturesService, ScreenService } from '@core/services';

import { ApplicationDialogsService } from '@shared/application-dialogs';
import { ComplaintFormModule } from '@shared/complaint-form/complaint-form.module';
import { IconComponent } from '@shared/icon/icon.component';
import { addDays, isSameDay } from 'date-fns';
import { Collection, Content, CSchool, TenantEnum, User, UserService } from 'lingo2-models';
import { FormatPipeModule } from 'lingo2-ngx-date-fns';
import { InViewportModule } from 'ng-in-viewport';
import { from, of, Subscription, tap } from 'rxjs';
import { bufferCount, concatMap, delay, takeUntil } from 'rxjs/operators';
import { CardContentTypes, CardContentView, GridCardService, SendToModalData } from './grid-card.service';
import { HorizontalCardComponent } from './horizontal/horizontal-card.component';
import { VerticalCardComponent } from './vertical/vertical-card.component';
import { VerticalSquareCardComponent } from './vertical-square/vertical-square-card.component';
import { WideHorizontalCardComponent } from './wide-horizontal/wide-horizontal-card.component';
import { WideVerticalCardComponent } from './wide-vertical/wide-vertical-card.component';

/* TODO. Review and move to srvdefaults
 */
const CONTENT_TYPE_MAP = {
  userServiceFilter: UserService,
  userFilter: User,
  contentFilter: Content,
  collectionsFilter: Collection,
  schoolsFilter: CSchool,
};

const CONTENT_TYPE_MAP_FOR_CARDS = {
  classroomsFilter: 'classrooms',
  schoolsFilter: 'school',
  collectionsFilter: 'collections',
  userFilter: 'tutors',
  userServiceFilter: 'user-service',
  meetingsFilter: 'meetings',
  contentFilter: 'lesson',
};
/*
 */
@Component({
  selector: 'app-grid-card-universal',
  templateUrl: './grid-card-universal.component.html',
  styleUrls: ['./grid-card-universal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,
    PipesModule,
    FormatPipeModule,
    OverlayModule,
    UserServiceCardModule,
    InViewportModule,
    ModalModule,
    UserServiceCardFullModule,
    MobileModalModule,
    VerticalCardComponent,
    VerticalSquareCardComponent,
    HorizontalCardComponent,
    WideVerticalCardComponent,
    WideHorizontalCardComponent,
    TeacherCardModule,
    SchoolCardModule,
    AccountSharedModule,
    ComplaintFormModule,
    CollectionCardModule,
    SchoolPageModule,
    TeacherBookingSurfaceBlockComponent,
    EmptyComponent,
    IconComponent,
  ],
  providers: [GridCardService],
})
export class GridCardUniversalComponent extends AbstractPane implements OnInit, OnDestroy {
  private _TAG = `[@@@] ${this.constructor.name}:`;

  protected screenService = inject(ScreenService);
  private location = inject(Location);
  gridCardService = inject(GridCardService);
  dialogsService = inject(ApplicationDialogsService);
  features = inject(FeaturesService);

  @ViewChild('cardHolder', { static: false }) cardHolder: ElementRef;
  @ViewChild('scroller', { static: false }) scroller: ElementRef;

  public filters: Date[];
  public indexFilterSelected = 0;
  public userServiceCardFullInModal: boolean;
  public openedUserService: UserService;
  public showCarousel = false;
  public surfaceBlockDisplay: boolean;
  public surfaceBlockDisplayService = true;
  public itemsPerRow: number;
  private originalUrl: string;
  public emptyObj: Partial<EmptyProps> = {
    text: 'favorites.empty-message',
    image: 'favorites',
  };

  public contentData: any[] = [];
  public contentType: CardContentTypes;
  public incrementalContent: any[] = [];
  private scrollPositionBeforeLoad = 0;
  private isInitialLoad = true;
  private lastLoadedCount = 0;

  private renderSub?: Subscription;
  private readonly batchSize = 3;
  private readonly batchDelayMs = 0;

  public ngOnDestroy(): void {
    this.renderSub?.unsubscribe();
    super.ngOnDestroy?.();
  }

  ngOnInit() {
    if (this.isFavoritesPage) {
      this.setEmptyContent();
    }
  }

  public onOpenCard(event) {
    this.gridCardService.setModalData(event);
    this.dialogsService.openGridCardModal(this.gridCardService.modalData);
    this.detectChanges();
  }

  /* Todo. Some strange things from legacy implementation
   */
  private makeup_content_data() {
    const type = this._pane?.payload__type; // todo. legacy bff1
    const content = this.getContentItems(); // todo. legacy bff1
    const Model = CONTENT_TYPE_MAP[type];

    if (!Model || !Array.isArray(content)) {
      return content || [];
    }
    return content.map((el) => new Model(el));
  }

  /*
   */
  protected override onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = this.hasContent();
    const newData = this.makeup_content_data();
    this.contentType = this.contentTypeFromFilter;
    this.markForCheck();
    this.scrollPositionBeforeLoad = window.pageYOffset || document.documentElement.scrollTop;
    if (viaQuery) {
      const newItems = newData.filter(
        (item) => !this.contentData.some((existing) => this.trackByFn(0, existing) === this.trackByFn(0, item)),
      );
      this.contentData = [...this.contentData, ...newItems];
    } else {
      this.contentData = [...newData];
      this.incrementalContent = [];
      this.lastLoadedCount = 0;
    }

    if (this.isFavoritesPage) {
      this.isVisible = true;
    }

    this.initFilter();
    this.renderInBatchesRx();

    if (!this.isInitialLoad) {
      setTimeout(() => {
        window.scrollTo({ top: this.scrollPositionBeforeLoad });
      }, 0);
    }
    this.isInitialLoad = false;

    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => this.calcCardInRow(width),
      error: (error) => {
        this.calcCardInRow(window.innerWidth);
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  /*
   */
  public selectFilter(index: number) {
    this.indexFilterSelected = index;
  }

  public userServicesFilter(): UserService[] {
    const selectedDate = this.filters?.[this.indexFilterSelected];
    if (!selectedDate) return [];

    return this.userServices?.filter((s) => s.created_at && isSameDay(new Date(s.created_at), selectedDate)) ?? [];
  }

  public onIinViewportChange(e) {
    this.surfaceBlockDisplay = e;
    this.surfaceBlockDisplayService = e;
    this.markForCheck();
  }

  // I'm not sure, but better don't block thread
  // Refactored legacy renderInBatches to RX
  private renderInBatchesRx() {
    this.renderSub?.unsubscribe();
    const newItems = this.contentData.slice(this.incrementalContent.length);
    this.renderSub = from(newItems)
      .pipe(
        bufferCount(this.batchSize),
        concatMap((batch, index) =>
          of(batch).pipe(
            delay(index === 0 ? 0 : this.batchDelayMs),
            tap(() => {
              // Используем push для добавления элементов
              this.incrementalContent.push(...batch);
              this.markForCheck();
            }),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {});
  }

  /*
   */
  private initFilter() {
    this.filters = Array.from({ length: 7 }, (item, index) => addDays(new Date(), index));
  }

  private setAndResetUrl(data: SendToModalData) {
    const url = data.data.route;
    const currentUrl = decodeURI(this.location.path(false));
    if (data.state) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  public get cardType() {
    return this.pane?.new_grid_view || CardContentView.vertical;
  }

  public get defaultItemsWidth() {
    return this.pane?.default_element_width || 320;
  }

  public get maxItemsWidth() {
    return this.pane?.max_element_width || 320;
  }

  public get maxItemsCount() {
    return this.pane?.max_element_count || 10;
  }

  /*
   */
  public calcCardInRow(width: number) {
    requestAnimationFrame(() => {
      const cardHolderWidth = this.cardHolder?.nativeElement?.clientWidth;
      const defaultW = this.defaultItemsWidth;
      const maxW = this.maxItemsWidth;
      const maxCount = this.maxItemsCount;
      const contentLength = this.contentData.length;

      let newItemsPerRow = 0;

      if (cardHolderWidth) {
        newItemsPerRow = Math.floor(cardHolderWidth / defaultW);
      } else {
        newItemsPerRow = Math.floor((width - (width < 1500 ? 130 : 300)) / defaultW);
      }

      if (contentLength < maxCount && contentLength < newItemsPerRow) {
        if (cardHolderWidth) {
          newItemsPerRow = Math.floor(cardHolderWidth / maxW);
        } else {
          newItemsPerRow = Math.floor((width - 90 - 40) / maxW);
        }
      }

      if (newItemsPerRow < 2) {
        newItemsPerRow = width < 500 ? 1 : 2;
      } else if (newItemsPerRow > maxCount) {
        newItemsPerRow = maxCount;
      }

      const newCarouselState = newItemsPerRow < contentLength;
      if (newItemsPerRow !== this.itemsPerRow || newCarouselState !== this.showCarousel) {
        this.itemsPerRow = newItemsPerRow;
        this.showCarousel = newCarouselState;
        this.markForCheck(); // this.detectChanges()
      }
    });
  }

  /*
   */
  public setEmptyContent() {
    const name = this.router.url.split('/').pop();
    this.emptyObj.title = `favorites/${name}.page-header-title`;
    this.emptyObj.link_label = this.router.url.substring(1) + '.empty';
    this.emptyObj.link = this.emptyObj.link2 = name === 'services' ? '/classes' : '/' + name;
  }

  public get isCarousel() {
    if (this.pane.show_like_slider) {
      return this.showCarousel;
    } else {
      return false;
    }
  }

  public closeModal() {
    this.gridCardService.resetModalData();
    this.setAndResetUrl(this.gridCardService.modalData);
    this.detectChanges();
  }

  public moveLeft(scroller) {
    scroller.nativeElement.scrollBy({
      left: -this.defaultItemsWidth + 30,
      behavior: 'smooth',
    });
  }

  public moveRight(scroller) {
    scroller.nativeElement.scrollBy({
      left: this.defaultItemsWidth + 30,
      behavior: 'smooth',
    });
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public get isFavoritesPage(): boolean {
    return this.router.url.includes('favorites');
  }

  public get contentTypeFromFilter() {
    for (const key of Object.keys(CONTENT_TYPE_MAP_FOR_CARDS)) {
      if (this._pane[key]) {
        return CONTENT_TYPE_MAP_FOR_CARDS[key];
      }
    }
    return 'lessons';
  }

  trackByFn(idx, item) {
    return item?.id || item?.slug || idx;
  }
}
